/**
 * <collapsible-read-more>
 * Caps the height of long collapsible-tab content (e.g. the PDP DETAILS tab) and
 * reveals the rest behind a "Read more" link. The clamp is applied with JS only,
 * so with JS disabled the full content renders (no link, nothing hidden).
 *
 * Markup:
 *   <collapsible-read-more data-max-height="280" data-more-text="Read more" data-less-text="Read less">
 *     <div class="collapsible__readmore-content"> ...rte content... </div>
 *     <button class="collapsible__readmore-toggle" aria-expanded="false" hidden>
 *       <span class="collapsible__readmore-text">Read more</span>
 *     </button>
 *   </collapsible-read-more>
 */
if (!customElements.get('collapsible-read-more')) {
  class CollapsibleReadMore extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('.collapsible__readmore-content');
      this.toggleBtn = this.querySelector('.collapsible__readmore-toggle');
      this.label = this.querySelector('.collapsible__readmore-text');
      if (!this.content || !this.toggleBtn) return;

      this.maxHeight = parseInt(this.dataset.maxHeight, 10) || 280;
      this.moreText = this.dataset.moreText || (this.label && this.label.textContent) || 'Read more';
      this.lessText = this.dataset.lessText || 'Read less';
      this.expanded = false;
      this.userToggled = false;

      this.onClick = this.toggle.bind(this);
      this.onReflow = () => {
        if (!this.userToggled) this.update();
      };

      this.toggleBtn.addEventListener('click', this.onClick);

      // Apply the initial clamp, then decide whether the link is needed.
      this.collapse(false);
      this.update();

      // Re-evaluate once late-loading media (images, iframes) change the height.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onReflow);
        this.resizeObserver.observe(this.content);
      }
      window.addEventListener('load', this.onReflow);
    }

    disconnectedCallback() {
      this.toggleBtn && this.toggleBtn.removeEventListener('click', this.onClick);
      this.resizeObserver && this.resizeObserver.disconnect();
      window.removeEventListener('load', this.onReflow);
    }

    get isOverflowing() {
      // scrollHeight reports full content height even while max-height clamps it.
      return this.content.scrollHeight > this.maxHeight + 4;
    }

    update() {
      const needed = this.isOverflowing;
      this.toggleBtn.hidden = !needed;
      this.classList.toggle('is-clampable', needed);

      if (!needed) {
        // Content fits within the cap: show everything, no clamp, no fade.
        this.content.style.maxHeight = 'none';
        this.classList.remove('is-clamped');
      } else if (!this.expanded) {
        this.content.style.maxHeight = this.maxHeight + 'px';
        this.classList.add('is-clamped');
      }
    }

    toggle(event) {
      if (event) event.preventDefault();
      this.userToggled = true;
      if (this.expanded) {
        this.collapse(true);
      } else {
        this.expand();
      }
    }

    expand() {
      this.expanded = true;
      this.classList.remove('is-clamped');
      this.classList.add('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      if (this.label) this.label.textContent = this.lessText;

      // Animate to the full height, then release the cap so nothing can clip.
      this.content.style.maxHeight = this.content.scrollHeight + 'px';
      const onEnd = (event) => {
        if (event.target !== this.content || event.propertyName !== 'max-height') return;
        this.content.removeEventListener('transitionend', onEnd);
        if (this.expanded) this.content.style.maxHeight = 'none';
      };
      this.content.addEventListener('transitionend', onEnd);
    }

    collapse(animate) {
      this.expanded = false;
      this.classList.add('is-clamped');
      this.classList.remove('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      if (this.label) this.label.textContent = this.moreText;

      if (animate) {
        // From a fixed start height so the transition runs, then down to the cap.
        this.content.style.maxHeight = this.content.scrollHeight + 'px';
        requestAnimationFrame(() => {
          this.content.style.maxHeight = this.maxHeight + 'px';
        });
      } else {
        this.content.style.maxHeight = this.maxHeight + 'px';
      }
    }
  }

  customElements.define('collapsible-read-more', CollapsibleReadMore);
}
